import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorFallback from './components/ErrorFallback';
import { ToastProvider } from './components/Toast';
import Dashboard from './pages/Dashboard';
import Player from './pages/Player';
import SuperAdmin from './pages/SuperAdmin';
import { SignIn, SignUp } from '@clerk/clerk-react';
import { setTokenProvider } from './lib/api';

function TokenProvider({ children }: { children: ReactNode }) {
  const { getToken } = useAuth();
  // Set synchronously on every render so it's always ready before children's effects fire
  setTokenProvider(() => getToken({ skipCache: true }));
  return <>{children}</>;
}

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

function MissingConfig() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="glass-card rounded-2xl p-8 max-w-lg text-center space-y-4">
        <h1 className="text-brand-yellow text-2xl font-bold">Configuration required</h1>
        <p className="text-white/70 text-sm">
          Create <code className="bg-white/10 px-1 rounded">apps/web/.env.local</code> with your
          Clerk publishable key:
        </p>
        <pre className="bg-white/5 border border-white/10 rounded-lg p-4 text-left text-xs text-brand-cyan">
          VITE_CLERK_PUBLISHABLE_KEY=pk_test_...
        </pre>
        <p className="text-white/40 text-xs">
          Get your key at <span className="text-brand-cyan">dashboard.clerk.com</span>
        </p>
      </div>
    </div>
  );
}

export default function App() {
  if (!PUBLISHABLE_KEY) return <MissingConfig />;

  return (
    <ClerkProvider publishableKey={PUBLISHABLE_KEY}>
      <TokenProvider>
        <ToastProvider>
          <BrowserRouter>
            <Routes>
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/play/:token"
                element={
                  <Sentry.ErrorBoundary
                    fallback={
                      <ErrorFallback
                        titleKey="playerError"
                        hintKey="playerErrorHint"
                        actionKey="reloadPlayer"
                      />
                    }
                  >
                    <Player />
                  </Sentry.ErrorBoundary>
                }
              />
              <Route path="/super-admin" element={<SuperAdmin />} />
              <Route
                path="/sign-in/*"
                element={
                  <div className="flex min-h-screen items-center justify-center">
                    <SignIn routing="path" path="/sign-in" />
                  </div>
                }
              />
              <Route
                path="/sign-up/*"
                element={
                  <div className="flex min-h-screen items-center justify-center">
                    <SignUp routing="path" path="/sign-up" />
                  </div>
                }
              />
            </Routes>
          </BrowserRouter>
        </ToastProvider>
      </TokenProvider>
    </ClerkProvider>
  );
}
